/**
 * Платформын адаптер — вэб (browser) болон мобайл (Capacitor) хоёрт НЭГ интерфейс.
 *
 * ⚠ Энэ файл @capacitor/* -ийг ИМПОРТ ХИЙХГҮЙ. Capacitor нь native bundle дотор
 * `window.Capacitor` global-ыг өөрөө шингээдэг тул түүгээр илрүүлнэ. Ингэснээр:
 *   - Capacitor суулгаагүй байхад ч вэб build хэвийн ажиллана (шинэ dependency үгүй),
 *   - вэбийн bundle-д мобайлын код орохгүй.
 *
 * Компонентууд платформыг ШУУД шалгахгүй — зөвхөн эндээс дуудна. Ингэснээр
 * дараа native боломж нэмэхэд компонент бүрийг өөрчлөхгүй.
 */

const getCapacitor = () =>
    typeof window !== "undefined" ? window.Capacitor : undefined;

/** Апп нь native бүрхүүл (iOS/Android) дотор ажиллаж байна уу. */
export const isNative = () => {
    const cap = getCapacitor();
    return typeof cap?.isNativePlatform === "function"
        ? cap.isNativePlatform()
        : false;
};

/** "ios" | "android" | "web" */
export const getPlatform = () => {
    const cap = getCapacitor();
    return typeof cap?.getPlatform === "function" ? cap.getPlatform() : "web";
};

/** Тухайн Capacitor plugin суулгагдсан бөгөөд боломжтой эсэх. */
export const hasPlugin = (name) => {
    const cap = getCapacitor();
    return typeof cap?.isPluginAvailable === "function"
        ? cap.isPluginAvailable(name)
        : false;
};

/**
 * Мобайл UI горим (header/breadcrumb нуух, 1 баганат layout г.м.).
 *
 * ⚠ Вэбийн зан төлөв ӨӨРЧЛӨГДӨХГҮЙ: зөвхөн одоо байгаа `?mobileApp=1` флаг
 * (webview бүрхүүл үүнийг ашигладаг) БОЛОН native дээр true болно. Хөтчийн цонх
 * нарийсахад автоматаар асахгүй — тэр нь одоогийн desktop хэрэглэгчдэд
 * гэнэтийн өөрчлөлт болох байсан. Responsive layout-ыг тусад нь CSS-ээр шийднэ.
 */
export const isMobileUI = () => {
    if (isNative()) return true;
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).get("mobileApp") === "1";
};

/**
 * Хэрэглэгч macOS дээр байгаа эсэх — ЗӨВХӨН гарын товчны тэмдэглэгээнд.
 *
 * Товчлуурын логик нь хоёр талдаа ажилладаг (`metaKey || ctrlKey`), гэхдээ
 * заавар нь платформдоо тохирох ёстой: Windows дээр "⌘K" гэж харуулбал
 * хэрэглэгч товчлуур нь байхгүй гэж ойлгоно.
 *
 * `navigator.platform` нь хуучирсан ч бүх хөтөч дээр ажилладаг тул
 * `userAgentData`-г эхэлж оролдоод түүн рүү унана.
 */
export const isMacOS = () => {
    if (typeof navigator === "undefined") return false;
    const platform = navigator.userAgentData?.platform || navigator.platform || "";
    return /mac/i.test(platform);
};

/**
 * Native дээр viewport-ийг апп маягаар тохируулна.
 *
 * WKWebView дээр давхар дарж томруулахыг (double-tap zoom) зөвхөн CSS-ийн
 * `touch-action: manipulation`-оор ЗОГСООЖ ЧАДДАГГҮЙ — viewport meta-г заавал
 * өөрчлөх шаардлагатай. Гэвч index.html-д шууд бичвэл ВЭБ хувилбарт ч нөлөөлж,
 * хөтчөөр үзэж буй хэрэглэгчийн томруулах эрхийг хасна.
 *
 * Иймд энд runtime-д, зөвхөн native илэрсэн үед л дарж бичнэ.
 * Апп эхлэхэд нэг удаа дуудна (App.js).
 */
export const applyNativeViewport = () => {
    if (!isNative() || typeof document === "undefined") return;
    const meta = document.querySelector('meta[name="viewport"]');
    if (!meta) return;
    // `viewport-fit=cover` — үүнгүйгээр `env(safe-area-inset-*)` нь ҮРГЭЛЖ 0 байдаг
    // тул AppBar нь Dynamic Island / notch-ийн бүсийг бүрхэж чадахгүй.
    meta.setAttribute(
        "content",
        "width=device-width, initial-scale=1, maximum-scale=1, minimum-scale=1, user-scalable=no, viewport-fit=cover"
    );
};

/**
 * API-ийн үндсэн хаяг.
 *
 * Вэб дээр хоосон ("") — одоогийнхтой ижил relative URL (`/api/v1/process`),
 * учир нь апп болон API нэг origin дээр байрладаг.
 *
 * Capacitor дээр апп нь `capacitor://localhost` (iOS) / `http://localhost`
 * (Android)-оос ачаалагддаг тул relative URL нь СЕРВЕР рүү биш, дотоод бандл руу
 * очно. Иймд `REACT_APP_API_BASE` заавал тохируулсан байх ёстой.
 */
export const getApiBase = () => {
    const configured = process.env.REACT_APP_API_BASE;
    if (configured) {
        return String(configured).replace(/\/+$/, ""); // сүүлийн "/"-г таслана
    }
    if (isNative() && process.env.NODE_ENV !== "production") {
        console.warn(
            "[platform] REACT_APP_API_BASE тохируулаагүй байна. Native дээр relative " +
            "API URL нь серверт хүрэхгүй (дотоод бандл руу очно)."
        );
    }
    return "";
};
